import * as React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { Match } from '@reach/router';
import cx from 'classnames';
import { Scrollbar, icons } from '@invertase/ui';

import { Navigator } from '../types/navigator';

import Link from './Link';
import useDarkMode from '../hooks/useDarkMode';

const { Education, Code, LightBulb } = icons;

function Sidebar() {
  const { toggleDarkMode } = useDarkMode();
  const { groups } = useStaticQuery(graphql`
    {
      groups: allNavigatorGroup {
        edges {
          node {
            match
            groups {
              items
              name
            }
          }
        }
      }
    }
  `);

  const nodes: Navigator[] = groups.edges.map((e: any) => e.node);

  return (
    <aside className="lg:sticky" style={{ top: '4rem' }}>
      <Scrollbar id="sidebar" className="overflow-y-auto" style={{ height: `calc(100vh - 4rem)` }}>
        <div className="py-6 lg:py-20">
          <ul>
            <MenuItem
              icon={<Education />}
              to="/react-native/docs/installation"
              text="Documentation"
            />
            <MenuItem icon={<Code />} to="/react-native/reference" text="Reference" />
            {/*<MenuItem*/}
            {/*  icon={<VideoCameraIcon />}*/}
            {/*  to="/react-native/screencasts"*/}
            {/*  text="Screencasts"*/}
            {/*/>*/}
          </ul>

          {nodes.map(node => (
            <Match key={node.match} path={node.match}>
              {props =>
                props.match ? (
                  <ul className="mt-6 mr-2">
                    {node.groups.map((group, i) => (
                      <React.Fragment key={`group-${i}`}>
                        <li className="mt-6 mb-2 text-gray-500 uppercase tracking-wider font-bold text-base lg:text-xs truncate">
                          {group.name}
                        </li>
                        {group.items.map(([to, text, icon]) => (
                          <li key={to}>
                            <Match path={`${to}/*`}>
                              {p => (
                                <Link
                                  to={to}
                                  className={cx(
                                    'flex items-center py-1 px-2 text-sm mb-1 truncate',
                                    {
                                      'bg-purple-200 dark:bg-white text-purple-500 dark:text-gray-800 rounded truncate':
                                        p.match,
                                      'hover:font-semibold': !p.match,
                                    },
                                  )}
                                >
                                  {icon ? (
                                    <img src={icon} alt="Icon" className="mr-2 w-5" />
                                  ) : (
                                    <span />
                                  )}
                                  <span dangerouslySetInnerHTML={{ __html: text }} />
                                </Link>
                              )}
                            </Match>
                          </li>
                        ))}
                      </React.Fragment>
                    ))}
                  </ul>
                ) : null
              }
            </Match>
          ))}
          <div className="mt-6 py-6 border-gray-300 dark:border-gray-800 border-t">
            <div
              role="button"
              className="outline-none flex items-center text-xs text-gray-800 dark:text-gray-500 hover:underline"
              onClick={() => toggleDarkMode()}
              onKeyPress={() => toggleDarkMode()}
              tabIndex={0}
            >
              <LightBulb size={16} className="mr-2 text-gray-900 dark:text-yellow-500" />
              Toggle Dark Mode
            </div>
          </div>
        </div>
      </Scrollbar>
    </aside>
  );
}

interface MenuItemProps {
  to: string;
  icon: React.ReactElement;
  text: string;
}

function MenuItem({ to, icon, text }: MenuItemProps) {
  return (
    <li className="flex items-center">
      <Link
        to={to}
        className="flex items-center rounded w-full mr-4 py-2 px-2 text-purple-800 dark:text-white hover:text-purple-600 dark-hover:bg-gray-800"
      >
        {React.cloneElement(icon, {
          size: 18,
        })}
        <span className="inline-block text-base tracking-wide font-semibold pl-3">{text}</span>
      </Link>
    </li>
  );
}

export default Sidebar;
